// ScreenshotThumbnail.js
// Renders a compact thumbnail strip for trade screenshots.
// Clicking any thumbnail opens a full-screen lightbox with navigation.
// Used in TradesView (trade list), JournalAI, and EditTradeModal.

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Image, Download, Pencil, Save } from 'lucide-react';
import { updateScreenshotAnalysis } from '../../services/screenshotService';

/**
 * @param {Object}   props
 * @param {Array}    props.screenshots     - Array of screenshot objects from the API
 * @param {number}   [props.maxVisible=3] - Max thumbnails to show before "+N more" badge
 * @param {string}   [props.className]
 */
const ScreenshotThumbnail = ({ screenshots = [], maxVisible = 3, className = '', fetchAllScreenshots }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [allScreenshots, setAllScreenshots] = useState(null);
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [isSavingAnalysis, setIsSavingAnalysis] = useState(false);

  const items = allScreenshots || screenshots;

  const openLightbox = async (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    if (fetchAllScreenshots && !allScreenshots) {
      try {
        const full = await fetchAllScreenshots();
        if (full && full.length > 0) setAllScreenshots(full);
      } catch (_) { /* keep using initial screenshots */ }
    }
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setIsEditingAnalysis(false);
  }, []);

  const prev = useCallback(() => {
    setIsEditingAnalysis(false);
    setActiveIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);

  const next = useCallback(() => {
    setIsEditingAnalysis(false);
    setActiveIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  const handleSaveAnalysis = async () => {
    if (!active?.id) return;
    setIsSavingAnalysis(true);
    try {
      await updateScreenshotAnalysis(active.id, editedAnalysis);
      // Update local state so the UI reflects the change immediately
      const updated = items.map((s, i) =>
        i === activeIndex ? { ...s, screenshot_analysis: editedAnalysis } : s
      );
      setAllScreenshots(updated);
      setIsEditingAnalysis(false);
    } catch (err) {
      console.error('[ScreenshotThumbnail] Save analysis failed:', err);
    } finally {
      setIsSavingAnalysis(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, prev, next, closeLightbox]);

  if (!screenshots || screenshots.length === 0) return null;

  const visible = screenshots.slice(0, maxVisible);
  const overflow = screenshots.length - maxVisible;
  const active = items[activeIndex];

  return (
    <>
      {/* Thumbnail strip */}
      <div className={`flex items-center gap-1.5 ${className}`}>
        {visible.map((shot, i) => (
          <button
            key={shot.id}
            onClick={(e) => { e.stopPropagation(); openLightbox(i); }}
            className="relative flex-shrink-0 w-14 h-10 rounded overflow-hidden border border-border hover:border-blue-500 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500"
            title={shot.file_name}
          >
            <img
              src={shot.public_url}
              alt={shot.file_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}

        {overflow > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); openLightbox(maxVisible); }}
            className="flex-shrink-0 w-14 h-10 rounded border border-border bg-muted hover:bg-accent transition-colors flex items-center justify-center text-xs font-medium text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            +{overflow}
          </button>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && active && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center"
          style={{ padding: '5vh 10vw' }}
          onClick={closeLightbox}
        >
          {/* Contained card — ~60vw wide centered, background visible on sides */}
          <div
            className="bg-card rounded-xl border-2 border-border shadow-2xl flex flex-col w-full h-full max-w-[90vw] sm:max-w-[60vw] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — always visible */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-border">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-foreground text-sm font-semibold whitespace-nowrap">
                  {activeIndex + 1} / {items.length}
                </span>
                <span className="text-muted-foreground text-sm truncate hidden sm:block">
                  {active.file_name}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={active.public_url}
                  download={active.file_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button
                  onClick={closeLightbox}
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Image + arrows — fills remaining space dynamically */}
            <div className="flex-1 min-h-0 relative flex items-center justify-center bg-black/40">
              <div className="w-full h-full flex items-center justify-center p-2 sm:p-3">
                <img
                  src={active.public_url}
                  alt={active.file_name}
                  className="max-w-full max-h-full object-contain rounded"
                />
              </div>

              {items.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-card hover:bg-muted border-2 border-border text-foreground flex items-center justify-center shadow-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-card hover:bg-muted border-2 border-border text-foreground flex items-center justify-center shadow-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>

            {/* Chart Analysis — scrollable, inside the card below the image */}
            {/* Show the section when there's analysis text OR when we have a real UUID (not the fake thumb id) */}
            {(active.screenshot_analysis || (active.id && /^[0-9a-f-]{36}$/i.test(String(active.id)))) && (
              <div className="flex-shrink-0 border-t-2 border-border max-h-[28vh] overflow-y-auto">
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 text-blue-400 font-semibold text-xs uppercase tracking-wide">
                      <Image className="w-3.5 h-3.5" />
                      Chart Analysis
                    </div>
                    {!isEditingAnalysis ? (
                      <button
                        onClick={() => { setEditedAnalysis(active.screenshot_analysis || ''); setIsEditingAnalysis(true); }}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-secondary border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                        Edit
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setIsEditingAnalysis(false)}
                          className="px-2.5 py-1 text-xs font-medium bg-secondary border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveAnalysis}
                          disabled={isSavingAnalysis}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <Save className="w-3 h-3" />
                          {isSavingAnalysis ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditingAnalysis ? (
                    <textarea
                      className="w-full bg-input border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-border leading-relaxed"
                      rows={5}
                      value={editedAnalysis}
                      onChange={(e) => setEditedAnalysis(e.target.value)}
                      placeholder="Add chart analysis notes..."
                    />
                  ) : (
                    <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">
                      {active.screenshot_analysis || <span className="text-muted-foreground italic">No analysis yet. Click Edit to add notes.</span>}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScreenshotThumbnail;
